// code-guardian-ignore-duplicates: parallel Sales & Targets tab layout to MultipliersTab (scope switch, add button, save/remove handlers) over a different API and fields
import { TableActionButton, TableActionGroup } from "@channelplay/table-actions";
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Loader2 } from 'lucide-react';
import { salesTargetsAPI } from '../../services/api';
import Modal from '../../components/Modal';
import { PageErrorBanner } from '../../components/PageErrorBanner';
import { BlockLoader } from '../../components/loading';
import { FormErrorBanner } from '../../components/FormErrorBanner';
import ConfirmDeleteModal from '../../components/ConfirmDeleteModal';
import {
  MATCH_OP_LABELS, VALUELESS_OPS, MAX_DEFAULT_START_DAY, coordKey, ordinalDay,
  type TargetDefault, type CoordinateOption, type MultiplierField,
  type SalesTargetConfig, type Scope,
} from './types';

/**
 * The safety net: what a store falls back to when its target was never set.
 *
 * ── WHY THIS EXISTS AT ALL ───────────────────────────────────────────────────
 * A monthly upload misses stores. New outlets open mid-month, a code gets
 * mistyped, a region is left off the sheet. Without a fallback those stores have
 * no target, which means the promoters in them have none either, and nobody
 * notices until the month is over.
 *
 * ── MOST SPECIFIC WINS ───────────────────────────────────────────────────────
 * A default may carry a condition on any store field ("Tier A stores default to
 * 100"). A conditional default beats the catch-all, and among conditional ones
 * the first by order wins — the same rule the multipliers use, so an admin
 * learns it once.
 *
 * A default is a STANDING figure, not a monthly one. It applies to every month
 * where nothing explicit was set, which is what makes it a safety net rather
 * than another thing to remember to fill in.
 *
 * ── STARTING PART-WAY THROUGH ────────────────────────────────────────────────
 * A default can be told to apply only from a day of the month ("from the 10th").
 * Days before it carry none of the default, and the WHOLE monthly figure is
 * spread over the days from it — the store is still held to the number in the
 * table, over fewer days. Up to the 28th, so the day exists in every month.
 */

interface Props {
  projectId: number;
  config: SalesTargetConfig;
}

const emptyDraft = (scopeType: Scope) => ({
  id: null as string | null,
  scopeType,
  coordKeyValue: '',
  targetValue: '',
  fieldSource: '' as 'column' | 'custom_field' | '',
  fieldKey: '',
  matchOp: 'eq',
  matchValue: '' as unknown,
  /** '' = the whole month, else a day 2–28. */
  activeFromDay: '',
});

/** Days a default can start from; the 1st is "the whole month". */
const START_DAYS = Array.from({ length: MAX_DEFAULT_START_DAY - 1 }, (_, i) => i + 2);

export default function DefaultsTab({ projectId, config }: Props) {
  const [scope, setScope] = useState<Scope>(config.sourceScope);
  const [defaults, setDefaults] = useState<TargetDefault[]>([]);
  const [coordinates, setCoordinates] = useState<CoordinateOption[]>([]);
  const [fields, setFields] = useState<MultiplierField[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<ReturnType<typeof emptyDraft> | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState<TargetDefault | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [defRes, coordRes, fieldRes] = await Promise.all([
        salesTargetsAPI.listDefaults(projectId, scope),
        salesTargetsAPI.listCoordinates(projectId, config.productGranularity),
        salesTargetsAPI.listMultiplierFields(projectId, scope),
      ]);
      setDefaults(defRes.data.data ?? []);
      setCoordinates(coordRes.data.data ?? []);
      setFields(fieldRes.data.data ?? []);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not load the defaults.');
    } finally {
      setLoading(false);
    }
  }, [projectId, scope, config.productGranularity]);

  useEffect(() => { void load(); }, [load]);

  const labelByCoord = useMemo(
    () => new Map(coordinates.map((c) => [coordKey(c.coord), c.label])),
    [coordinates],
  );

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setFormError(null);
    try {
      const coord = coordinates.find((c) => coordKey(c.coord) === draft.coordKeyValue);
      if (!coord) throw new Error('Choose a product.');
      const hasCondition = draft.fieldSource !== '' && draft.fieldKey !== '';
      const value = !hasCondition || VALUELESS_OPS.has(draft.matchOp) ? null
        : draft.matchOp === 'in' || draft.matchOp === 'not_in' || draft.matchOp === 'between'
          ? String(draft.matchValue ?? '').split(',').map((v) => v.trim()).filter(Boolean)
          : draft.matchValue;
      const body = {
        scopeType: draft.scopeType,
        productId: coord.coord.productId,
        skuId: coord.coord.skuId,
        targetValue: Number(draft.targetValue),
        // Always sent: the API leaves the start day alone only when it is absent.
        activeFromDay: draft.activeFromDay === '' ? null : Number(draft.activeFromDay),
        ...(hasCondition
          ? { fieldSource: draft.fieldSource, fieldKey: draft.fieldKey, matchOp: draft.matchOp, matchValue: value }
          : {}),
      };
      if (draft.id) await salesTargetsAPI.updateDefault(projectId, draft.id, body);
      else await salesTargetsAPI.createDefault(projectId, body);
      setDraft(null);
      await load();
    } catch (err: any) {
      setFormError(err.response?.data?.message || err.message || 'Could not save the default.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await salesTargetsAPI.deleteDefault(projectId, deleting.id);
      setDeleting(null);
      await load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not delete the default.');
    }
  };

  const describe = (d: TargetDefault): string => {
    if (!d.fieldKey) return `Every ${scope === 'store' ? 'store' : 'promoter'}`;
    const op = MATCH_OP_LABELS[d.matchOp ?? ''] ?? d.matchOp ?? '';
    const value = Array.isArray(d.matchValue) ? d.matchValue.join(', ') : String(d.matchValue ?? '');
    return `${d.fieldLabel ?? d.fieldKey} ${op}${value ? ` ${value}` : ''}`;
  };

  if (loading) {
    return (
      <BlockLoader className="rounded-xl border border-gray-200 bg-white py-20" />
    );
  }

  return (
    <div className="space-y-4">
      {error && <PageErrorBanner message={error} />}

      {!config.applyDefaults && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          Defaults are switched off for this project, so nothing here is being applied. A store with
          no target simply has none. Turn them on in Settings if you want them used.
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-gray-600">For</span>
          <div className="flex rounded-lg border border-gray-200 bg-gray-50 p-0.5">
            {(['store', 'employee'] as Scope[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setScope(s)}
                className={`rounded-md px-3 py-1 text-sm font-medium ${
                  scope === s ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-600'
                }`}
              >
                {s === 'store' ? 'Store targets' : 'Promoter targets'}
              </button>
            ))}
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setDraft(emptyDraft(scope)); setFormError(null); }}
          disabled={coordinates.length === 0}
          className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          Add a default
        </button>
      </div>

      {defaults.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
          <h3 className="text-base font-medium text-gray-700">No defaults set</h3>
          <p className="mx-auto mt-2 max-w-xl text-sm text-gray-500">
            {coordinates.length === 0
              ? 'Add products to this project’s Product Master first — a default is a figure for a product.'
              : 'A store missed out of the monthly upload currently has no target at all. Add a '
                + 'default and it will fall back to that instead, so nobody is left without a number.'}
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="px-4 py-3">Product</th>
                <th className="px-4 py-3">Applies to</th>
                <th className="px-4 py-3">Active from</th>
                <th className="px-4 py-3 text-right">Monthly target</th>
                <th className=" px-4 py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {defaults.map((d) => (
                <tr key={d.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">
                    {d.productLabel || labelByCoord.get(coordKey(d.coord)) || '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {describe(d)}
                    {!d.fieldKey && (
                      <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                        catch-all
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">
                    {d.activeFromDay ? (
                      <span title="Days before this carry none of the default; the whole figure is spread over the days from it.">
                        The {ordinalDay(d.activeFromDay)} onwards
                      </span>
                    ) : (
                      <span className="text-gray-400">Whole month</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-900">
                    {d.targetValue.toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <TableActionGroup>
                      <TableActionButton action="edit" label="Edit"
                        type="button"
                        title="Edit"
                        onClick={() => {
                          setDraft({
                            id: d.id,
                            scopeType: d.scopeType,
                            coordKeyValue: coordKey(d.coord),
                            targetValue: String(d.targetValue),
                            fieldSource: d.fieldSource ?? '',
                            fieldKey: d.fieldKey ?? '',
                            matchOp: d.matchOp ?? 'eq',
                            matchValue: Array.isArray(d.matchValue) ? d.matchValue.join(', ') : d.matchValue,
                            activeFromDay: d.activeFromDay ? String(d.activeFromDay) : '',
                          });
                          setFormError(null);
                        }} />
                      <TableActionButton action="delete" label="Delete"
                        type="button"
                        title="Delete"
                        onClick={() => setDeleting(d)} />
                    </TableActionGroup>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Edit default' : 'Add a default'}
        size="lg"
        footer={
          <div className="flex w-full justify-end gap-2">
            <button
              type="button"
              onClick={() => setDraft(null)}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || !draft?.coordKeyValue || draft?.targetValue === ''}
              className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save
            </button>
          </div>
        }
      >
        {draft && (
          <div className="space-y-4">
            {formError && <FormErrorBanner message={formError} />}

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="def-product">
                  Product
                </label>
                <select
                  id="def-product"
                  value={draft.coordKeyValue}
                  onChange={(e) => setDraft({ ...draft, coordKeyValue: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                >
                  <option value="">Choose a product…</option>
                  {coordinates.map((c) => (
                    <option key={coordKey(c.coord)} value={coordKey(c.coord)}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="def-value">
                  Monthly target
                </label>
                <input
                  id="def-value"
                  type="number"
                  min={0}
                  step="any"
                  value={draft.targetValue}
                  onChange={(e) => setDraft({ ...draft, targetValue: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="def-active-from">
                Active from
              </label>
              <select
                id="def-active-from"
                value={draft.activeFromDay}
                onChange={(e) => setDraft({ ...draft, activeFromDay: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 sm:w-1/2"
              >
                <option value="">The 1st — the whole month</option>
                {START_DAYS.map((day) => (
                  <option key={day} value={String(day)}>The {ordinalDay(day)} of the month</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                {draft.activeFromDay === ''
                  ? `Every day of the month carries this default for a ${scope === 'store' ? 'store' : 'promoter'} with no target.`
                  : `Days before the ${ordinalDay(Number(draft.activeFromDay))} carry none of it. The whole monthly `
                    + `target is spread over the days from the ${ordinalDay(Number(draft.activeFromDay))} to the `
                    + 'end of the month, so the month’s target stays the figure above.'}
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 p-3">
              <p className="text-sm font-medium text-gray-900">Only for some {scope === 'store' ? 'stores' : 'promoters'}?</p>
              <p className="mt-0.5 text-xs text-gray-500">
                Leave the field blank for a catch-all that covers everything. A default with a
                condition beats the catch-all.
              </p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <select
                  value={`${draft.fieldSource}::${draft.fieldKey}`}
                  onChange={(e) => {
                    const [source, ...rest] = e.target.value.split('::');
                    setDraft({
                      ...draft,
                      fieldSource: source as 'column' | 'custom_field' | '',
                      fieldKey: rest.join('::'),
                      matchValue: '',
                    });
                  }}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
                >
                  <option value="::">No condition</option>
                  {fields.map((f) => (
                    <option key={`${f.source}::${f.key}`} value={`${f.source}::${f.key}`}>{f.label}</option>
                  ))}
                </select>
                <select
                  value={draft.matchOp}
                  disabled={!draft.fieldKey}
                  onChange={(e) => setDraft({ ...draft, matchOp: e.target.value })}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:bg-gray-50"
                >
                  {['eq', 'neq', 'in', 'not_in', 'gt', 'gte', 'lt', 'lte', 'between', 'blank', 'not_blank'].map((op) => (
                    <option key={op} value={op}>{MATCH_OP_LABELS[op]}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={String(draft.matchValue ?? '')}
                  disabled={!draft.fieldKey || VALUELESS_OPS.has(draft.matchOp)}
                  onChange={(e) => setDraft({ ...draft, matchValue: e.target.value })}
                  placeholder="Value"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100 disabled:bg-gray-50"
                />
              </div>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDeleteModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={remove}
        isSubmitting={false}
        title="Delete this default?"
        entityLabel="default"
        entityName={deleting ? `${deleting.productLabel ?? ''} → ${deleting.targetValue}` : ''}
        consequenceNote={
          'Any store relying on it loses its fallback and will show as having no target at all.'
        }
      />
    </div>
  );
}
